import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import {
    apiSendChatMessage,
    apiGetChatHistory,
    apiListChats,
    apiUpdateChatSession,
} from 'services/ChatService'
import {
    DEFAULT_CHAT_SETTINGS,
    getChatPreviewText,
    isWelcomeMessage,
    normalizeChatAttachments,
    normalizeChatSettings,
} from './chatUtils'

export const sendChatMessage = createAsyncThunk(
    'chat/apiSendChatMessage',
    async (params) => {
        const response = await apiSendChatMessage(params)
        return response.data
    }
)

export const fetchChatHistory = createAsyncThunk(
    'chat/apiGetChatHistory',
    async (params) => {
        const response = await apiGetChatHistory(params)
        return response.data
    }
)

export const fetchChats = createAsyncThunk(
    'chat/apiListChats',
    async () => {
        const response = await apiListChats()
        return response.data
    }
)

export const updateChatSession = createAsyncThunk(
    'chat/apiUpdateChatSession',
    async (params) => {
        const response = await apiUpdateChatSession(params)
        return response.data
    }
)

const makeWelcomeMessage = () => ({
    id: `welcome-${nanoid()}`,
    text: "Hello! I'm your chat assistant. How can I help you today?",
    isUser: false,
    timestamp: new Date().toISOString(),
})

const normalizeMessage = (message) => {
    const role = message?.isUser ? 'user' : 'assistant'
    const timestamp = message?.timestamp || new Date().toISOString()
    const text = typeof message?.text === 'string' ? message.text : String(message?.text ?? '')

    return {
        ...message,
        id: message?.id || `${role}-${timestamp}-${text.slice(0, 48)}`,
        text,
        isUser: Boolean(message?.isUser),
        timestamp,
        attachments: normalizeChatAttachments(message?.attachments),
        artifacts: Array.isArray(message?.artifacts) ? message.artifacts : [],
    }
}

const getMeaningfulMessages = (messages = []) => {
    return Array.isArray(messages)
        ? messages.filter((message) => !isWelcomeMessage(message))
        : []
}

const getLatestTimestamp = (...values) => {
    return values
        .filter(Boolean)
        .sort((left, right) => new Date(right) - new Date(left))[0] || null
}

const hydrateChatRecord = (chat = {}, options = {}) => {
    const createdAt = chat?.createdAt || options.createdAt || new Date().toISOString()
    const context = chat?.context || null
    const messages = Array.isArray(chat?.messages)
        ? chat.messages.map(normalizeMessage)
        : options.includeWelcomeMessage
            ? [makeWelcomeMessage()]
            : []
    const meaningfulMessages = getMeaningfulMessages(messages)
    const lastMessage = meaningfulMessages[meaningfulMessages.length - 1] || null
    const explicitCount = Number(chat?.messageCount)
    const messageCount = Number.isFinite(explicitCount)
        ? explicitCount
        : meaningfulMessages.length
    const hasMessages =
        typeof chat?.hasMessages === 'boolean'
            ? chat.hasMessages || meaningfulMessages.length > 0
            : messageCount > 0
    const updatedAt =
        chat?.updatedAt ||
        getLatestTimestamp(context?.updatedAt, lastMessage?.timestamp, createdAt) ||
        createdAt

    return {
        title:
            chat?.title ||
            context?.title ||
            context?.activeContext?.label ||
            options.title ||
            'New chat',
        loading: Boolean(chat?.loading),
        createdAt,
        updatedAt,
        context,
        settings: normalizeChatSettings(
            chat?.settings || context?.settings || options.settings || DEFAULT_CHAT_SETTINGS
        ),
        messages,
        hasMessages,
        messageCount,
        lastMessagePreview: getChatPreviewText({ ...chat, messages }),
        lastMessageRole:
            chat?.lastMessageRole ||
            (lastMessage ? (lastMessage.isUser ? 'user' : 'assistant') : null),
    }
}

const refreshChatRecord = (chat) => {
    const meaningfulMessages = getMeaningfulMessages(chat.messages)
    const lastMessage = meaningfulMessages[meaningfulMessages.length - 1] || null

    chat.settings = normalizeChatSettings(chat.settings || chat.context?.settings)
    chat.title =
        chat.title ||
        chat.context?.title ||
        chat.context?.activeContext?.label ||
        'New chat'
    chat.messageCount = meaningfulMessages.length
    chat.hasMessages = meaningfulMessages.length > 0 || Boolean(chat.hasMessages)
    chat.lastMessagePreview = getChatPreviewText(chat)
    chat.lastMessageRole = lastMessage
        ? lastMessage.isUser
            ? 'user'
            : 'assistant'
        : chat.lastMessageRole || null
    chat.updatedAt =
        getLatestTimestamp(
            chat.updatedAt,
            chat.context?.updatedAt,
            lastMessage?.timestamp,
            chat.createdAt
        ) || chat.createdAt
}

const appendMessage = (state, id, message) => {
    const normalized = normalizeMessage(message)
    const exists = state.chats[id].messages.some((item) => item.id && item.id === normalized.id)
    if (!exists) {
        state.chats[id].messages.push(normalized)
    }
    refreshChatRecord(state.chats[id])
}

const initialState = {
    chats: {}, // id -> { messages: [], loading: false, createdAt, settings, context }
    order: [], // most recent first ids
    currentChatId: null,
    hasHydratedList: false,
}

const normalizeChatRecord = (chat) => ({
    ...hydrateChatRecord(chat),
})

const ensureChat = (state, id) => {
    if (!state.chats[id]) {
        state.chats[id] = hydrateChatRecord(
            {
                messages: [makeWelcomeMessage()],
                loading: false,
                context: null,
                settings: DEFAULT_CHAT_SETTINGS,
            },
            { includeWelcomeMessage: true, title: 'New chat' }
        )
        // put newest first
        state.order = [id, ...state.order.filter((x) => x !== id)]
    }
}

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        startNewChat: (state, action) => {
            const providedId = action.payload // optional string id
            // confirm id is a string or integer, else generate new
            const id =
                typeof providedId === 'string' || typeof providedId === 'number'
                    ? providedId.toString()
                    : nanoid()
            ensureChat(state, id)
            state.currentChatId = id
        },
        selectChat: (state, action) => {
            const id = String(action.payload || '')
            if (!id) {
                return
            }
            ensureChat(state, id)
            state.currentChatId = id
        },
        addMessage: (state, action) => {
            const { id, message } = action.payload
            ensureChat(state, id)
            appendMessage(state, id, message)
            // move chat to front on activity
            state.order = [id, ...state.order.filter((x) => x !== id)]
        },
        setChatHistory: (state, action) => {
            const { id, messages } = action.payload
            if (!id) {
                return
            }

            if (!state.chats[id]) {
                state.chats[id] = hydrateChatRecord({}, { includeWelcomeMessage: true })
                state.order = [id, ...state.order.filter((x) => x !== id)]
            }

            state.chats[id].messages =
                Array.isArray(messages) && messages.length > 0
                    ? messages.map(normalizeMessage)
                    : state.chats[id].messages?.length > 0
                        ? state.chats[id].messages
                        : [makeWelcomeMessage()]
            if (action.payload?.context) {
                state.chats[id].context = action.payload.context
                state.chats[id].settings = normalizeChatSettings(
                    action.payload.context?.settings || state.chats[id].settings
                )
            }
            if (action.payload?.title) {
                state.chats[id].title = action.payload.title
            }
            refreshChatRecord(state.chats[id])
        },
        setLoading: (state, action) => {
            const { id, loading } = action.payload
            ensureChat(state, id)
            state.chats[id].loading = loading
        },
        resetChat: (state, action) => {
            const id = action.payload
            if (id && state.chats[id]) {
                delete state.chats[id]
                state.order = state.order.filter((x) => x !== id)
                if (state.currentChatId === id) {
                    state.currentChatId = null
                }
            }
        },
        updateChatSessionState: (state, action) => {
            const { id, settings, context, title } = action.payload || {}
            if (!id) {
                return
            }

            ensureChat(state, id)
            if (settings) {
                state.chats[id].settings = normalizeChatSettings(settings)
            }
            if (context) {
                state.chats[id].context = {
                    ...(state.chats[id].context || {}),
                    ...context,
                    settings: normalizeChatSettings(
                        context?.settings || settings || state.chats[id].settings
                    ),
                }
            }
            if (title) {
                state.chats[id].title = title
            }
            refreshChatRecord(state.chats[id])
        },
        clearAllChats: () => initialState,
    },
    extraReducers: {
        [fetchChats.fulfilled]: (state, action) => {
            const chats = action.payload?.data?.chats || {}
            const fetchedOrder = action.payload?.data?.order || []
            const nextChats = Object.entries(chats).reduce((acc, [id, chat]) => {
                acc[id] = normalizeChatRecord(chat)
                return acc
            }, {})

            Object.entries(state.chats).forEach(([id, chat]) => {
                if (!nextChats[id]) {
                    nextChats[id] = normalizeChatRecord(chat)
                }
            })

            state.chats = nextChats
            state.order = [
                ...fetchedOrder,
                ...state.order.filter((id) => !fetchedOrder.includes(id) && nextChats[id]),
            ]
            state.hasHydratedList = true
        },
        [sendChatMessage.fulfilled]: (state, action) => {
            const { id, title, message, context } = action.payload.data
            if (!id || !message) {
                return
            }
            ensureChat(state, id)
            appendMessage(state, id, message)
            state.chats[id].loading = false
            if (context) {
                state.chats[id].context = context
                state.chats[id].settings = normalizeChatSettings(
                    context?.settings || state.chats[id].settings
                )
            }
            if (title) {
                state.chats[id].title = title
            }
            refreshChatRecord(state.chats[id])
            state.order = [id, ...state.order.filter((x) => x !== id)]
        },
        [sendChatMessage.pending]: (state, action) => {
            const id = action.meta?.arg?.id || state.currentChatId
            if (id) {
                ensureChat(state, id)
                state.chats[id].loading = true
                if (action.meta?.arg?.settings) {
                    state.chats[id].settings = normalizeChatSettings(
                        action.meta.arg.settings
                    )
                }
            }
        },
        [sendChatMessage.rejected]: (state, action) => {
            const id = action.meta?.arg?.id || state.currentChatId
            if (id) {
                ensureChat(state, id)
                state.chats[id].loading = false
            }
        },
        [fetchChatHistory.fulfilled]: (state, action) => {
            const { id, title, messages, context } = action.payload.data
            if (!id) {
                return
            }
            if (!state.chats[id]) {
                state.chats[id] = hydrateChatRecord({}, { includeWelcomeMessage: true })
                state.order = [id, ...state.order.filter((x) => x !== id)]
            }
            state.chats[id].messages =
                Array.isArray(messages) && messages.length > 0
                    ? messages.map(normalizeMessage)
                    : state.chats[id].messages?.length > 0
                        ? state.chats[id].messages
                        : [makeWelcomeMessage()]
            if (context) {
                state.chats[id].context = context
                state.chats[id].settings = normalizeChatSettings(
                    context?.settings || state.chats[id].settings
                )
            }
            if (title) {
                state.chats[id].title = title
            }
            refreshChatRecord(state.chats[id])
        },
        [updateChatSession.fulfilled]: (state, action) => {
            const { id, context } = action.payload.data || {}
            if (!id || !context) {
                return
            }

            ensureChat(state, id)
            state.chats[id].context = context
            state.chats[id].settings = normalizeChatSettings(
                context?.settings || state.chats[id].settings
            )
            if (context?.title) {
                state.chats[id].title = context.title
            }
            refreshChatRecord(state.chats[id])
        },
    },
})

export const {
    startNewChat,
    selectChat,
    addMessage,
    setChatHistory,
    setLoading,
    resetChat,
    updateChatSessionState,
    clearAllChats,
} = chatSlice.actions

export default chatSlice.reducer
